import type { QueryClient } from "@tanstack/react-query";
import type { PersistentChatMessageData, PersistentChatWithMessages } from "~shared/types/persistent-chat-types";
import { chatQueryKey } from "./PersistentChatTypes";

export const DIFFERENT_APPROACH_RETRY_MESSAGE =
  "That answer didn't work for me. Please try again with a different approach.";

export function patchChatFeedbackRatingInCache(
  queryClient: QueryClient,
  params: {
    projectId: string;
    chatId: string;
    messageId: string;
    rating: number;
  },
): void {
  queryClient.setQueryData(
    chatQueryKey(params.projectId, params.chatId),
    (prev: PersistentChatWithMessages | undefined) => {
      if (!prev) {
        return prev;
      }

      const nextFeedbackByMessageId = { ...(prev.feedbackByMessageId ?? {}) };
      if (params.rating === 0) {
        delete nextFeedbackByMessageId[params.messageId];
      } else {
        nextFeedbackByMessageId[params.messageId] = params.rating;
      }

      return {
        ...prev,
        feedbackByMessageId: nextFeedbackByMessageId,
      };
    },
  );
}

export function countNegativeFeedbackInChat(
  feedbackByMessageId: Record<string, number> | undefined,
): number {
  if (!feedbackByMessageId) {
    return 0;
  }
  return Object.values(feedbackByMessageId).filter((rating) => rating === -1).length;
}

export function shouldShowRepeatedDislikeRecovery(
  feedbackByMessageId: Record<string, number> | undefined,
  includePendingDislike: boolean,
): boolean {
  const existing = countNegativeFeedbackInChat(feedbackByMessageId);
  return existing + (includePendingDislike ? 1 : 0) >= 2;
}

export function findPrecedingUserMessageId(
  messages: PersistentChatMessageData[],
  assistantMessageId: string,
): string | null {
  const assistantIndex = messages.findIndex((message) => message.id === assistantMessageId);
  if (assistantIndex <= 0) {
    return null;
  }

  for (let index = assistantIndex - 1; index >= 0; index -= 1) {
    if (messages[index]?.role === "user") {
      return messages[index].id;
    }
  }

  return null;
}
